import { useState, useEffect } from 'react';
import { adminAPI, appointmentsAPI } from '@/lib/api';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  Users, 
  Clock,
  BarChart3,
  AlertTriangle,
  MessageCircle,
  ChevronRight,
  Cake,
  Heart,
  RotateCcw,
  Check,
  X,
  CheckCheck,
  Tag
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import NotificationSettings from '@/components/NotificationSettings';
import TelegramSettings from '@/components/TelegramSettings';
import IOSProfileDownload from '@/components/IOSProfileDownload';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState({ openInvoices: [], activeChats: 0, escalatedChats: 0 });
  const [pendingDiscounts, setPendingDiscounts] = useState([]);
  const [approvingId, setApprovingId] = useState(null);

  useEffect(() => {
    fetchData();
    fetchPendingDiscounts();
    // Refresh alerts every 30 seconds
    const interval = setInterval(() => { fetchAlerts(); fetchPendingDiscounts(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      // Get customers with negative balance
      const customersRes = await api.get('/customers');
      const customersWithDebt = customersRes.data
        .filter(c => c.balance < 0)
        .sort((a, b) => a.balance - b.balance)
        .slice(0, 5);
      
      // Get active/escalated chat count
      const chatCountRes = await api.get('/admin/chat/active-count');
      
      setAlerts({
        openInvoices: customersWithDebt,
        activeChats: chatCountRes.data.active || 0,
        escalatedChats: chatCountRes.data.escalated || 0
      });
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, todayRes] = await Promise.all([
        adminAPI.getDashboardStats(),
        appointmentsAPI.getTodayAppointments(),
      ]);
      setStats(statsRes.data);
      setTodayAppointments(todayRes.data);
      await fetchAlerts();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingDiscounts = async () => {
    try {
      const res = await api.get('/admin/auto-discount-pending');
      setPendingDiscounts(res.data || []);
    } catch (e) {}
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    try {
      await api.post(`/admin/auto-discount-pending/${id}/approve`);
      toast.success('Rabatt-E-Mail gesendet');
      fetchPendingDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/admin/auto-discount-pending/${id}/reject`);
      toast.success('Abgelehnt');
      fetchPendingDiscounts();
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const handleApproveAll = async () => {
    setApprovingId('all');
    try {
      const res = await api.post('/admin/auto-discount-pending/approve-all');
      toast.success(res.data?.message || 'Alle gesendet');
      fetchPendingDiscounts();
    } catch (err) {
      toast.error('Fehler beim Senden');
    } finally {
      setApprovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  const hasAlerts = alerts.openInvoices.length > 0 || alerts.activeChats > 0 || alerts.escalatedChats > 0 || pendingDiscounts.length > 0;

  const typeIcons = { birthday: Cake, loyalty: Heart, reactivation: RotateCcw };
  const typeColors = { birthday: 'pink', loyalty: 'red', reactivation: 'blue' };
  const typeLabels = { birthday: 'Geburtstag', loyalty: 'Treue', reactivation: 'Reaktivierung' };

  return (
    <div data-testid="admin-dashboard">
      {/* Alerts Section */}
      {hasAlerts && (
        <div className="mb-6 space-y-3" data-testid="admin-alerts">
          {/* Chat Alerts */}
          {(alerts.activeChats > 0 || alerts.escalatedChats > 0) && (
            <Link to="/admin/chats">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between hover:bg-blue-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">
                      {alerts.escalatedChats > 0 
                        ? `${alerts.escalatedChats} Chat${alerts.escalatedChats > 1 ? 's' : ''} braucht Ihre Hilfe`
                        : `${alerts.activeChats} aktive Chat-Anfrage${alerts.activeChats > 1 ? 'n' : ''}`
                      }
                    </p>
                    <p className="text-sm text-blue-600">Klicken um Chats anzuzeigen</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-blue-400" />
              </div>
            </Link>
          )}

          {/* Pending Auto-Discount Approvals */}
          {pendingDiscounts.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4" data-testid="pending-discounts-alert">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#9B1B30] rounded-full flex items-center justify-center">
                    <Tag className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-purple-900">
                      {pendingDiscounts.length} Rabatt-E-Mail{pendingDiscounts.length > 1 ? 's' : ''} warten auf Freigabe
                    </p>
                    <p className="text-sm text-purple-600">Ohne Ihre Freigabe wird keine E-Mail gesendet</p>
                  </div>
                </div>
                {pendingDiscounts.length > 1 && (
                  <Button
                    size="sm"
                    className="bg-[#9B1B30] hover:bg-[#7D1526] text-white"
                    onClick={handleApproveAll}
                    disabled={approvingId === 'all'}
                    data-testid="approve-all-btn"
                  >
                    <CheckCheck className="w-4 h-4 mr-1" />
                    {approvingId === 'all' ? 'Sendet...' : 'Alle freigeben'}
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {pendingDiscounts.map((p) => {
                  const Icon = typeIcons[p.type] || Tag;
                  const color = typeColors[p.type] || 'gray';
                  return (
                    <div key={p.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon className={`w-4 h-4 text-${color}-500`} />
                        <div>
                          <p className="font-medium text-sm text-gray-900">{p.customer_name}</p>
                          <p className="text-xs text-gray-500">
                            {typeLabels[p.type] || p.type} — {p.discount_code} — {p.value_text}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => handleApprove(p.id)}
                          disabled={approvingId === p.id}
                          data-testid={`approve-${p.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleReject(p.id)}
                          data-testid={`reject-${p.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Open Invoices Alerts */}
          {alerts.openInvoices.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-amber-900">Offene Rechnungen</p>
                  <p className="text-sm text-amber-600">{alerts.openInvoices.length} Kunde{alerts.openInvoices.length > 1 ? 'n' : ''} mit negativem Saldo</p>
                </div>
              </div>
              <div className="space-y-2 ml-13">
                {alerts.openInvoices.map((customer) => (
                  <Link 
                    key={customer.id} 
                    to="/admin/kunden"
                    className="flex items-center justify-between bg-white p-3 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{customer.first_name} {customer.last_name}</p>
                      <p className="text-sm text-gray-500">{customer.customer_number}</p>
                    </div>
                    <span className="font-bold text-red-600">
                      {customer.balance.toFixed(2).replace('.', ',')} €
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-barlow font-bold text-3xl text-[#1A1A1A] mb-2">
          Dashboard
        </h1>
        <p className="font-manrope text-gray-600">
          Übersicht über Ihr Geschäft · {format(new Date(), "EEEE, d. MMMM yyyy", { locale: de })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Gesamttermine - Links to list page */}
        <Link to="/admin/termine-liste">
          <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-total-appointments">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-sm text-gray-500">Gesamttermine</p>
                  <p className="font-barlow font-bold text-3xl text-[#1A1A1A]">
                    {stats?.total_appointments || 0}
                  </p>
                  <p className="font-manrope text-xs text-[#9B1B30]">Zur Liste →</p>
                </div>
                <div className="w-14 h-14 bg-[#9B1B30]/10 rounded-full flex items-center justify-center">
                  <Calendar className="w-7 h-7 text-[#9B1B30]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Heute - Links to today page */}
        <Link to="/admin/termine-heute">
          <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-today-appointments">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-sm text-gray-500">Heute</p>
                  <p className="font-barlow font-bold text-3xl text-[#1A1A1A]">
                    {stats?.today_appointments || 0}
                  </p>
                  <p className="font-manrope text-xs text-[#2E2A88]">Zur Liste →</p>
                </div>
                <div className="w-14 h-14 bg-[#2E2A88]/10 rounded-full flex items-center justify-center">
                  <Clock className="w-7 h-7 text-[#2E2A88]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/kunden">
          <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-total-customers">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-sm text-gray-500">Kunden</p>
                  <p className="font-barlow font-bold text-3xl text-[#1A1A1A]">
                    {stats?.total_customers || 0}
                  </p>
                </div>
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Users className="w-7 h-7 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/kunden?filter=open_balance">
          <Card className="card-hover cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-open-balances">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-manrope text-sm text-gray-500">Offene Rechnungen</p>
                  <p className="font-barlow font-bold text-3xl text-red-600">
                    {alerts.openInvoices?.length || 0}
                  </p>
                  <p className="font-manrope text-xs text-gray-400">Kunden mit Schulden</p>
                </div>
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Today's Appointments Quick View */}
      <Card data-testid="today-appointments-card">
        <CardHeader>
          <CardTitle className="font-barlow text-xl flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#9B1B30]" />
            Heutige Termine ({todayAppointments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todayAppointments.length > 0 ? (
            <div className="space-y-4">
              {todayAppointments.map((apt) => (
                <Link to={`/admin/termine?date=${apt.date}`} key={apt.id}>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#9B1B30] text-white rounded-lg flex items-center justify-center">
                        <span className="font-barlow font-bold text-lg">{apt.time.split(':')[0]}</span>
                      </div>
                      <div>
                        <p className="font-manrope font-medium">{apt.customer_name}</p>
                        <p className="font-manrope text-sm text-gray-500">
                          <span className="text-[#9B1B30] font-medium">{apt.customer_number}</span> · {apt.time} Uhr · {apt.duration} Min
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-manrope text-gray-500">Keine Termine für heute</p>
            </div>
          )}}
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <TelegramSettings />
        <NotificationSettings />
        <IOSProfileDownload />
      </div>
    </div>
  );
}
